import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, CalendarCheck2, CheckCircle2, ChevronLeft, ChevronRight, Clock, CreditCard, Eye, EyeOff, Hotel, LogIn, LogOut, Pencil, Plus, Scissors, Sparkles, Trash2, Truck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Appointment, Customer, Daycare, Dog, Service, Stay } from '../../../../../packages/contracts/src/index.js';
import { ActionMenu } from '../../components/ui/action-menu';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { ConfirmDialog, type ConfirmState } from '../../components/ui/confirm';
import { Select } from '../../components/ui/field';
import { PaginationControls, useControlledPagination } from '../../components/ui/pagination';
import { CustomerSearchBox } from '../../components/ui/search';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { ErrorState, LoadingState } from '../../components/ui/state';
import { TimeRange } from '../../components/ui/time-range';
import { Table, Td, Th } from '../../components/ui/table';
import { api } from '../../shared/api';
import { formatDate, formatDateTime } from '../../shared/format';
import { toDateInput } from '../../shared/listFilters';
import { useAuth } from '../auth/AuthProvider';

type CalendarEvent = {
  readonly id: string;
  readonly date: string;
  readonly startAt: string;
  readonly endAt?: string;
  readonly title: string;
  readonly detail: string;
  readonly href: string;
  readonly tone: 'appointment' | 'daycare' | 'stay';
  readonly customerId: string;
  readonly customerName: string;
  readonly dogIds: readonly string[];
  readonly dogNames: string;
  readonly hasTransportService: boolean;
  readonly source: Appointment | Daycare | Stay;
};

type CalendarView = 'month' | 'week' | 'day';
type TimeFormat = '24h' | '12h';

export function OperationsCalendarPage() {
  const { user } = useAuth();
  const isAdministrator = user?.groups.includes('Administrator') ?? false;
  const queryClient = useQueryClient();
  const [anchorDate, setAnchorDate] = useState(() => new Date());
  const [calendarView, setCalendarView] = useState<CalendarView>('month');
  const [typeFilter, setTypeFilter] = useState<'all' | CalendarEvent['tone']>('all');
  const [transportOnly, setTransportOnly] = useState(false);
  const [customerFilter, setCustomerFilter] = useState('');
  const [timeFormat, setTimeFormat] = useState<TimeFormat>('24h');
  const [calendarHidden, setCalendarHidden] = useState(false);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsPageSize, setEventsPageSize] = useState(10);
  const [selectedDate, setSelectedDate] = useState(() => toDateInput(new Date()));
  const [detailsEvent, setDetailsEvent] = useState<CalendarEvent | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const now = useCurrentMinute();
  const range = useMemo(() => calendarRange(anchorDate, calendarView), [anchorDate, calendarView]);
  const appointmentsQuery = useQuery({ queryKey: ['calendar', 'appointments', range], queryFn: () => api.appointments.list({ limit: 100, from: range.from, to: range.to }) });
  const staysQuery = useQuery({ queryKey: ['calendar', 'stays', range], queryFn: () => api.stays.list({ limit: 100, from: range.from, to: range.to }) });
  const daycaresQuery = useQuery({ queryKey: ['calendar', 'daycares', range], queryFn: () => api.daycares.list({ limit: 100, from: range.from, to: range.to }) });
  const dogsQuery = useQuery({ queryKey: ['dogs', 'calendar'], queryFn: () => api.dogs.list({ limit: 100 }) });
  const customersQuery = useQuery({ queryKey: ['customers', 'calendar'], queryFn: () => api.customers.list({ limit: 100 }) });
  const servicesQuery = useQuery({ queryKey: ['services', 'calendar'], queryFn: () => api.services.list({ limit: 100 }) });

  const days = useMemo(() => calendarDays(anchorDate, calendarView), [anchorDate, calendarView]);
  const events = useMemo(() => buildEvents({
    appointments: appointmentsQuery.data?.items ?? [],
    daycares: daycaresQuery.data?.items ?? [],
    dogs: dogsQuery.data?.items ?? [],
    customers: customersQuery.data?.items ?? [],
    services: servicesQuery.data?.items ?? [],
    stays: staysQuery.data?.items ?? [],
  }), [appointmentsQuery.data?.items, customersQuery.data?.items, daycaresQuery.data?.items, dogsQuery.data?.items, servicesQuery.data?.items, staysQuery.data?.items]);
  const filteredEvents = useMemo(() => events.filter((event) => {
    if (typeFilter !== 'all' && event.tone !== typeFilter) return false;
    if (transportOnly && !event.hasTransportService) return false;
    if (customerFilter && event.customerId !== customerFilter) return false;
    return true;
  }), [customerFilter, events, transportOnly, typeFilter]);
  const sortedListEvents = useMemo(() => sortEventsNewestFirst(filteredEvents), [filteredEvents]);
  const listEventsPage = useControlledPagination(sortedListEvents, eventsPage, setEventsPage, eventsPageSize);
  const eventsByDate = useMemo(() => groupEvents(filteredEvents), [filteredEvents]);
  const selectedDayEvents = useMemo(() => sortEvents(eventsByDate.get(selectedDate) ?? []), [eventsByDate, selectedDate]);
  const isLoading = appointmentsQuery.isLoading || staysQuery.isLoading || daycaresQuery.isLoading || dogsQuery.isLoading || customersQuery.isLoading || servicesQuery.isLoading;
  const error = appointmentsQuery.error ?? staysQuery.error ?? daycaresQuery.error ?? dogsQuery.error ?? customersQuery.error ?? servicesQuery.error;

  useEffect(() => {
    setEventsPage(1);
  }, [customerFilter, eventsPageSize, range.from, range.to, transportOnly, typeFilter]);

  const moveRange = (offset: number) => setAnchorDate((current) => {
    const next = new Date(current);
    if (calendarView === 'month') next.setMonth(next.getMonth() + offset, 1);
    if (calendarView === 'week') next.setDate(next.getDate() + offset * 7);
    if (calendarView === 'day') next.setDate(next.getDate() + offset);
    return next;
  });
  const setQuickView = (view: 'today' | 'week' | 'month') => {
    const now = new Date();
    if (view === 'today') {
      setCalendarView('day');
      setAnchorDate(now);
      setSelectedDate(toDateInput(now));
    }
    if (view === 'week') {
      setCalendarView('week');
      setAnchorDate(now);
      setSelectedDate(toDateInput(now));
    }
    if (view === 'month') {
      setCalendarView('month');
      setAnchorDate(new Date(now.getFullYear(), now.getMonth(), 1));
    }
  };
  const today = toDateInput(new Date());
  const customers = customersQuery.data?.items ?? [];
  const invalidateCalendar = async () => {
    await queryClient.invalidateQueries({ queryKey: ['calendar'] });
    await queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
  };
  const appointmentDone = useMutation({ mutationFn: api.appointments.markDone, onSuccess: invalidateCalendar });
  const appointmentDelete = useMutation({ mutationFn: api.appointments.remove, onSuccess: invalidateCalendar });
  const stayCheckOut = useMutation({ mutationFn: api.stays.checkOut, onSuccess: invalidateCalendar });
  const stayDelete = useMutation({ mutationFn: api.stays.remove, onSuccess: invalidateCalendar });
  const stayExtend = useMutation({ mutationFn: ({ id, checkOutDate }: { readonly id: string; readonly checkOutDate: string }) => api.stays.update(id, { checkOutDate }), onSuccess: invalidateCalendar });
  const daycareCheckOut = useMutation({ mutationFn: api.daycares.checkOut, onSuccess: invalidateCalendar });
  const daycareDelete = useMutation({ mutationFn: api.daycares.remove, onSuccess: invalidateCalendar });
  const daycareExtend = useMutation({ mutationFn: ({ id, endAt }: { readonly id: string; readonly endAt: string }) => api.daycares.update(id, { endAt }), onSuccess: invalidateCalendar });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-950">Calendario</h1>
          <p className="mt-1 text-sm text-slate-500">Vista operativa de citas, hotel y guarderia.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" onClick={() => setCalendarHidden((current) => !current)}>
            {calendarHidden ? <Eye className="h-4 w-4" aria-hidden="true" /> : <EyeOff className="h-4 w-4" aria-hidden="true" />}
            {calendarHidden ? 'Mostrar calendario' : 'Ocultar calendario'}
          </Button>
          <Button type="button" variant="secondary" className="h-9 w-9 px-0" onClick={() => moveRange(-1)} aria-label="Periodo anterior">
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button type="button" variant="secondary" onClick={() => setAnchorDate(new Date())}>Hoy</Button>
          <Button type="button" variant="secondary" className="h-9 w-9 px-0" onClick={() => moveRange(1)} aria-label="Periodo siguiente">
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="grid gap-3">
            <div className="flex flex-wrap items-center justify-end gap-3">
              <div className="flex flex-wrap gap-2 text-xs text-[#4f5d49]">
                <span className="inline-flex items-center gap-1 rounded-md border border-[#d6ded0] bg-[#eef3ea] px-2 py-1"><Hotel className="h-3.5 w-3.5" /> Hotel</span>
                <span className="inline-flex items-center gap-1 rounded-md border border-[#d6ded0] bg-[#f8eee9] px-2 py-1"><Clock className="h-3.5 w-3.5" /> Guarderia</span>
                <span className="inline-flex items-center gap-1 rounded-md border border-[#d6ded0] bg-white px-2 py-1"><Scissors className="h-3.5 w-3.5" /> Grooming</span>
                <span className="inline-flex items-center gap-1 rounded-md border border-[#d6ded0] bg-[#f4eef8] px-2 py-1"><Sparkles className="h-3.5 w-3.5" /> Spa</span>
                <span className="inline-flex items-center gap-1 rounded-md border border-[#d6ded0] bg-[#edf5f7] px-2 py-1"><Truck className="h-3.5 w-3.5" /> Transporte</span>
              </div>
            </div>
            <div className="grid gap-2">
              <div className="grid min-w-0 gap-2 md:grid-cols-3">
                <div className="grid gap-1.5">
                  <span className="px-1 text-[10px] font-semibold uppercase tracking-wide text-[#687461]">Vista</span>
                  <SegmentedControl value={calendarView} options={[{ id: 'day', label: 'Dia' }, { id: 'week', label: 'Semana' }, { id: 'month', label: 'Mes' }]} onChange={(nextView) => nextView === 'day' ? (setCalendarView('day'), setSelectedDate(toDateInput(anchorDate))) : setQuickView(nextView)} ariaLabel="Vista del calendario" />
                </div>
                <div className="grid gap-1.5">
                  <span className="px-1 text-[10px] font-semibold uppercase tracking-wide text-[#687461]">Tipo</span>
                  <SegmentedControl value={typeFilter} options={[{ id: 'all', label: 'Todo' }, { id: 'stay', label: 'Hotel' }, { id: 'daycare', label: 'Guarderia' }, { id: 'appointment', label: 'Citas' }]} onChange={setTypeFilter} ariaLabel="Filtrar calendario por tipo" />
                </div>
                <div className="grid gap-1.5">
                  <span className="px-1 text-[10px] font-semibold uppercase tracking-wide text-[#687461]">Formato</span>
                  <SegmentedControl value={timeFormat} options={[{ id: '24h', label: '24 h' }, { id: '12h', label: '12 h' }]} onChange={setTimeFormat} ariaLabel="Formato de hora" />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="secondary" onClick={() => setQuickView('today')}>Hoy</Button>
                <Button type="button" variant={transportOnly ? 'primary' : 'secondary'} onClick={() => setTransportOnly((current) => !current)}>Con transporte</Button>
              </div>
              <div className="min-w-0">
                <CustomerSearchBox value={customerFilter} customers={customers} placeholder="Filtrar por cliente" onChange={setCustomerFilter} />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? <LoadingState label="Cargando calendario" /> : null}
          {error ? <ErrorState message={error.message} /> : null}
          {!isLoading && !error ? (
            <div className="grid gap-4">
              {!calendarHidden ? <CalendarMainGrid
                calendarView={calendarView}
                days={days}
                events={filteredEvents}
                now={now}
                onShowDetails={setDetailsEvent}
                onSelectDay={(day) => {
                  setSelectedDate(day.date);
                  if (!day.inMonth) setAnchorDate(new Date(`${day.date}T12:00:00`));
                }}
                selectedDate={selectedDate}
                timeFormat={timeFormat}
                title={calendarTitle(anchorDate, calendarView)}
                today={today}
              /> : null}
              <DayDetailsPanel
                date={selectedDate}
                selectedDayEvents={selectedDayEvents}
                events={listEventsPage.pageItems}
                totalEvents={sortedListEvents.length}
                eventsPagination={listEventsPage}
                onEventsPageChange={listEventsPage.setPage}
                onEventsPageSizeChange={(nextPageSize) => {
                  setEventsPageSize(nextPageSize);
                  setEventsPage(1);
                }}
                rangeFrom={range.from}
                rangeTo={range.to}
                onShowDetails={setDetailsEvent}
                timeFormat={timeFormat}
                onConfirm={setConfirm}
                actions={{
                  appointmentDone,
                  appointmentDelete,
                  stayCheckOut,
                  stayDelete,
                  stayExtend,
                  daycareCheckOut,
                  daycareDelete,
                  daycareExtend,
                }}
              />
            </div>
          ) : null}
          {[appointmentDone, appointmentDelete, stayCheckOut, stayDelete, stayExtend, daycareCheckOut, daycareDelete, daycareExtend].some((mutation) => mutation.isPending) ? (
            <p className="mt-3 text-sm text-[#687461]"><span className="mr-2 inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#d6ded0] border-t-[#334033]" />Actualizando calendario...</p>
          ) : null}
          {[appointmentDone, appointmentDelete, stayCheckOut, stayDelete, stayExtend, daycareCheckOut, daycareDelete, daycareExtend].find((mutation) => mutation.isError)?.error ? (
            <p className="mt-3 text-sm text-rose-600">{[appointmentDone, appointmentDelete, stayCheckOut, stayDelete, stayExtend, daycareCheckOut, daycareDelete, daycareExtend].find((mutation) => mutation.isError)?.error?.message}</p>
          ) : null}
        </CardContent>
      </Card>
      <EventDetailsDialog event={detailsEvent} showCompletedBy={isAdministrator} onClose={() => setDetailsEvent(null)} />
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function CalendarEventPill({ event, timeFormat }: { readonly event: CalendarEvent; readonly timeFormat: TimeFormat }) {
  const toneClass = event.tone === 'stay' ? 'border-[#c8d9c0] bg-[#eef3ea]/80' : event.tone === 'daycare' ? 'border-[#e7cabf] bg-[#f8eee9]/80' : 'border-[#d6ded0] bg-white/80';
  return (
    <span className={`rounded-md border px-2 py-1 text-xs text-[#334033] ${toneClass}`}>
      <span className="flex items-center gap-1 font-semibold">
        <CalendarEventIcon event={event} className="h-3.5 w-3.5" />
        {event.title}
      </span>
      <span className="mt-0.5 block font-mono text-[11px] text-[#687461]">{eventTimeLabel(event, timeFormat)}</span>
    </span>
  );
}

function CalendarMainGrid({
  calendarView,
  days,
  events,
  now,
  onShowDetails,
  onSelectDay,
  selectedDate,
  timeFormat,
  title,
  today,
}: {
  readonly calendarView: CalendarView;
  readonly days: readonly { readonly date: string; readonly inMonth: boolean }[];
  readonly events: readonly CalendarEvent[];
  readonly now: Date;
  readonly onShowDetails: (event: CalendarEvent) => void;
  readonly onSelectDay: (day: { readonly date: string; readonly inMonth: boolean }) => void;
  readonly selectedDate: string;
  readonly timeFormat: TimeFormat;
  readonly title: string;
  readonly today: string;
}) {
  if (calendarView === 'day') {
    const day = days[0];
    const dayEvents = sortEvents(events.filter((event) => eventDates(event).includes(day.date)));
    return (
      <div className="overflow-hidden rounded-md border border-[#d6ded0] bg-white">
        <CalendarRangeHeading title={title} />
        <div className="hidden border-b border-[#eef3ea] bg-[#fbfcfa] px-3 py-1 text-xs font-semibold uppercase leading-tight text-[#687461] md:block">{weekdayLabel(new Date(`${day.date}T12:00:00`))}</div>
        <div className="relative min-h-40 border-b border-r border-[#eef3ea] p-2 pt-11 text-left transition md:min-h-[36rem] bg-[#eef3ea] ring-2 ring-inset ring-[#75816a]">
          <CalendarDayNumber day={day} today={today} />
          <button type="button" className="absolute inset-0" aria-label={`Seleccionar ${formatDate(day.date)}`} onClick={() => onSelectDay(day)} />
          <TimedEventBlocks date={day.date} events={dayEvents} maxEvents={14} now={now} onShowDetails={onShowDetails} showTimeLabels timeFormat={timeFormat} />
        </div>
      </div>
    );
  }

  const weeks = chunkWeeks(days);
  return (
    <div className="overflow-hidden rounded-md border border-[#d6ded0] bg-white">
      <CalendarRangeHeading title={title} />
      <div className="hidden grid-cols-7 md:grid">
        {['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'].map((label) => (
          <div key={label} className="border-b border-[#eef3ea] bg-[#fbfcfa] px-3 py-2 text-xs font-semibold uppercase text-[#687461]">{label}</div>
        ))}
      </div>
      {weeks.map((week) => {
        const layout = weekEventLayout(week, events, calendarView === 'month' ? 3 : 5);
        return (
          <div key={week[0].date} className={`relative grid grid-cols-7 border-b border-[#eef3ea] last:border-b-0 ${calendarView === 'week' ? 'min-h-[36rem]' : 'min-h-56'}`}>
            {week.map((day, dayIndex) => {
              const selected = selectedDate === day.date;
              const hiddenCount = layout.hiddenByDate.get(day.date) ?? 0;
              const dayEvents = calendarView === 'week' ? sortEvents(events.filter((event) => eventDates(event).includes(day.date))) : [];
              return (
                <div
                  key={day.date}
                  className={`relative border-r border-[#eef3ea] p-2 pt-11 text-left transition hover:bg-[#eef3ea] last:border-r-0 ${selected ? 'bg-[#eef3ea] ring-2 ring-inset ring-[#75816a]' : day.inMonth ? 'bg-white' : 'bg-[#fbfcfa] text-[#8b9485]'}`}
                >
                  <button type="button" className="absolute inset-0" aria-label={`Seleccionar ${formatDate(day.date)}`} onClick={() => onSelectDay(day)} />
                  <CalendarDayNumber day={day} today={today} />
                  {calendarView === 'week' ? <TimedEventBlocks date={day.date} events={dayEvents} maxEvents={6} now={now} onShowDetails={onShowDetails} showTimeLabels={dayIndex === 0} timeFormat={timeFormat} /> : null}
                  {calendarView === 'month' && hiddenCount > 0 ? <span className="absolute left-2 right-2 rounded-md border border-[#d6ded0] bg-white px-2 py-1 text-xs font-semibold text-[#4f5d49]" style={{ top: `${48 + layout.visibleLaneCount * 44}px` }}>+{hiddenCount} mas</span> : null}
                </div>
              );
            })}
            {calendarView === 'month' ? (
              <div className="pointer-events-none absolute inset-x-0 top-11 z-10">
                {layout.segments.map((segment) => <CalendarEventBar key={`${segment.event.tone}-${segment.event.id}-${segment.startIndex}-${segment.lane}`} segment={segment} onShowDetails={onShowDetails} timeFormat={timeFormat} />)}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function CalendarRangeHeading({ title }: { readonly title: string }) {
  return <div className="border-b border-[#d6ded0] bg-[#f8faf6] px-4 py-4"><h2 className="text-2xl font-semibold capitalize text-[#223126] sm:text-3xl">{title}</h2></div>;
}

function CalendarEventIcon({ event, endpoint, className }: { readonly event: CalendarEvent; readonly endpoint?: CalendarBarSegment['endpoint']; readonly className: string }) {
  if (event.tone === 'stay') {
    const Icon = endpoint === 'start' ? LogIn : endpoint === 'end' ? LogOut : Hotel;
    return <Icon className={className} aria-hidden="true" />;
  }
  if (event.tone === 'daycare') return <Clock className={className} aria-hidden="true" />;
  const category = (event.source as Appointment).serviceCategory;
  const Icon = category === 'transportation' ? Truck : category === 'spa' ? Sparkles : category === 'grooming' ? Scissors : CalendarCheck2;
  return <Icon className={className} aria-hidden="true" />;
}

function TimedEventBlocks({
  date,
  events,
  maxEvents,
  now,
  onShowDetails,
  showTimeLabels,
  timeFormat,
}: {
  readonly date: string;
  readonly events: readonly CalendarEvent[];
  readonly maxEvents: number;
  readonly now: Date;
  readonly onShowDetails: (event: CalendarEvent) => void;
  readonly showTimeLabels: boolean;
  readonly timeFormat: TimeFormat;
}) {
  const hasMultiDayEvent = events.some((event) => eventDateStart(event) < date || eventDateEnd(event) > date);
  const firstHour = events.length ? Math.min(...events.map((event) => clampedEventRangeForDate(event, date).start.getHours())) : 8;
  const startHour = hasMultiDayEvent ? 0 : Math.max(0, firstHour);
  const hourSpan = hasMultiDayEvent ? 24 : 8;
  const layout = timedEventLayout(date, events, startHour, hourSpan);
  const visibleEvents = layout.slice(0, maxEvents);
  const extraCount = Math.max(0, layout.length - visibleEvents.length);
  const chunks = Array.from({ length: hourSpan * 2 + 1 }, (_, index) => index);

  return (
    <div className="absolute inset-x-2 bottom-2 top-11 overflow-hidden">
      <TimelineGridLines />
      {showTimeLabels ? (
        <div className="pointer-events-none absolute inset-y-0 left-0 w-12 border-r border-[#d6ded0] bg-white/75">
          {chunks.map((chunk) => {
            const hour = startHour + Math.floor(chunk / 2);
            const minute = chunk % 2 === 0 ? '00' : '30';
            return (
              <span key={chunk} className="absolute right-1 -mt-2 font-mono text-[9px] text-[#687461]" style={{ top: `${(chunk / (hourSpan * 2)) * 100}%` }}>
                {formatClockParts(hour, minute, timeFormat)}
              </span>
            );
          })}
        </div>
      ) : null}
      {date === toDateInput(now) ? <CurrentHourMarker now={now} startHour={startHour} hourSpan={hourSpan} timeFormat={timeFormat} /> : null}
      <div className={`absolute inset-y-0 right-1 ${showTimeLabels ? 'left-14' : 'left-1'}`}>
        {visibleEvents.map((item) => (
          <button
            key={`${item.event.tone}-${item.event.id}-timed-${item.lane}`}
            type="button"
            className={`absolute z-20 overflow-hidden rounded-md border px-2 py-1.5 text-left text-xs shadow-sm transition hover:-translate-y-0.5 hover:brightness-95 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#75816a] ${eventToneClass(item.event)}`}
            style={{
              left: `${(item.lane / item.laneCount) * 100}%`,
              top: `${item.top}%`,
              width: `calc(${100 / item.laneCount}% - 4px)`,
              height: `${item.height}%`,
              minHeight: '32px',
            }}
            title={`${item.event.title} - ${eventTimeLabel(item.event, timeFormat)}`}
            onClick={(event) => {
              event.stopPropagation();
              onShowDetails(item.event);
            }}
          >
            <span className="flex items-start gap-1.5 font-semibold leading-tight"><CalendarEventIcon event={item.event} className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span className="break-words">{item.event.title}</span></span>
            <span className="block truncate font-mono text-[11px] opacity-75">{eventTimeLabel(item.event, timeFormat)}</span>
          </button>
        ))}
      </div>
      {extraCount > 0 ? <span className="absolute bottom-0 left-1 right-1 z-20 rounded-md border border-[#d6ded0] bg-white px-2 py-1 text-xs font-semibold text-[#4f5d49]">+{extraCount} mas</span> : null}
    </div>
  );
}

function CalendarDayNumber({ day, today }: { readonly day: { readonly date: string; readonly inMonth: boolean }; readonly today: string }) {
  return (
    <div className="absolute left-2 top-2 flex items-center justify-between gap-2">
      <span className={`grid h-7 w-7 place-items-center rounded-md text-sm font-semibold ${day.date === today ? 'bg-[#334033] text-white' : 'text-[#334033]'}`}>{Number(day.date.slice(-2))}</span>
      <span className="text-xs font-medium text-[#687461] md:hidden">{formatDate(day.date)}</span>
    </div>
  );
}

type CalendarBarSegment = {
  readonly event: CalendarEvent;
  readonly lane: number;
  readonly startIndex: number;
  readonly span: number;
  readonly continuesBefore: boolean;
  readonly continuesAfter: boolean;
  readonly endpoint: 'start' | 'end' | 'single';
};

function CalendarEventBar({ segment, onShowDetails, timeFormat }: { readonly segment: CalendarBarSegment; readonly onShowDetails: (event: CalendarEvent) => void; readonly timeFormat: TimeFormat }) {
  const toneClass = eventToneClass(segment.event);
  const radiusClass = 'rounded-md';
  return (
    <button
      type="button"
      className={`pointer-events-auto absolute h-10 overflow-hidden border px-2 py-1 text-left text-[11px] font-semibold shadow-sm transition hover:brightness-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#75816a] ${toneClass} ${radiusClass}`}
      style={{
        left: `calc(${(segment.startIndex / 7) * 100}% + 8px)`,
        top: `${segment.lane * 44}px`,
        width: `calc(${(segment.span / 7) * 100}% - 10px)`,
      }}
      title={`${segment.event.title} - ${eventTimeLabel(segment.event, timeFormat)}`}
      onClick={(event) => {
        event.stopPropagation();
        onShowDetails(segment.event);
      }}
    >
      <span className="flex items-start gap-1.5 leading-4"><CalendarEventIcon event={segment.event} endpoint={segment.endpoint} className="h-3.5 w-3.5 shrink-0" /><span className="line-clamp-2 break-words">{segment.event.title}</span></span>
    </button>
  );
}

function chunkWeeks(days: readonly { readonly date: string; readonly inMonth: boolean }[]) {
  const weeks: Array<Array<{ readonly date: string; readonly inMonth: boolean }>> = [];
  for (let index = 0; index < days.length; index += 7) weeks.push([...days.slice(index, index + 7)]);
  return weeks;
}

type CalendarBarCandidate = Omit<CalendarBarSegment, 'lane'> & { readonly endIndex: number; readonly start: string };

function weekEventLayout(week: readonly { readonly date: string }[], events: readonly CalendarEvent[], maxLanes: number) {
  const weekStart = week[0].date;
  const weekEnd = week[week.length - 1].date;
  const candidates: CalendarBarCandidate[] = events
    .flatMap((event): CalendarBarCandidate[] => {
      const start = eventDateStart(event);
      const end = eventDateEnd(event);
      if (end < weekStart || start > weekEnd) return [];
      if (start === end) { const index = daysBetween(weekStart, start); return index >= 0 && index <= 6 ? [{ event, endIndex: index, start, startIndex: index, span: 1, continuesBefore: false, continuesAfter: false, endpoint: 'single' as const }] : []; }
      const endpoints: CalendarBarCandidate[] = [];
      if (start >= weekStart && start <= weekEnd) { const index = daysBetween(weekStart, start); endpoints.push({ event, endIndex: index, start, startIndex: index, span: 1, continuesBefore: false, continuesAfter: true, endpoint: 'start' as const }); }
      if (end >= weekStart && end <= weekEnd) { const index = daysBetween(weekStart, end); endpoints.push({ event, endIndex: index, start, startIndex: index, span: 1, continuesBefore: true, continuesAfter: false, endpoint: 'end' as const }); }
      return endpoints;
    })
    .sort((left, right) => left.startIndex - right.startIndex || right.span - left.span || new Date(left.event.startAt).getTime() - new Date(right.event.startAt).getTime());

  const laneEnds: number[] = [];
  const visible: CalendarBarSegment[] = [];
  const hiddenByDate = new Map<string, number>();

  for (const candidate of candidates) {
    const lane = firstAvailableLane(laneEnds, candidate.startIndex);
    for (let index = candidate.startIndex; index <= candidate.endIndex; index += 1) {
      const date = week[index].date;
      if (lane >= maxLanes) hiddenByDate.set(date, (hiddenByDate.get(date) ?? 0) + 1);
    }
    if (lane >= maxLanes) continue;
    laneEnds[lane] = candidate.endIndex;
    visible.push({ ...candidate, lane });
  }

  return { hiddenByDate, segments: visible, visibleLaneCount: Math.min(maxLanes, Math.max(0, ...visible.map((segment) => segment.lane + 1))) };
}

function firstAvailableLane(laneEnds: readonly number[], startIndex: number) {
  const lane = laneEnds.findIndex((endIndex) => endIndex < startIndex);
  return lane === -1 ? laneEnds.length : lane;
}

function eventDateStart(event: CalendarEvent) {
  return toDateInput(new Date(event.startAt));
}

function eventDateEnd(event: CalendarEvent) {
  return toDateInput(new Date(event.endAt ?? event.startAt));
}

function daysBetween(start: string, end: string) {
  return Math.round((new Date(`${end}T12:00:00`).getTime() - new Date(`${start}T12:00:00`).getTime()) / 86_400_000);
}

type TimedEventPosition = {
  readonly event: CalendarEvent;
  readonly endMinutes: number;
  readonly height: number;
  readonly lane: number;
  readonly laneCount: number;
  readonly startMinutes: number;
  readonly top: number;
};

function timedEventLayout(date: string, events: readonly CalendarEvent[], startHour: number, hourSpan: number): TimedEventPosition[] {
  const laneEnds: number[] = [];
  return sortEvents(events)
    .map((event) => {
      const { start, end } = clampedEventRangeForDate(event, date);
      const rawStartMinutes = (start.getHours() - startHour) * 60 + start.getMinutes();
      const rawEndMinutes = (end.getHours() - startHour) * 60 + end.getMinutes();
      const startMinutes = Math.max(0, Math.min(hourSpan * 60, rawStartMinutes));
      const endMinutes = Math.max(startMinutes + 30, Math.min(hourSpan * 60, rawEndMinutes || startMinutes + 30));
      return { endMinutes, event, startMinutes };
    })
    .sort((left, right) => left.startMinutes - right.startMinutes || right.endMinutes - left.endMinutes)
    .map((item) => {
      const lane = firstAvailableTimeLane(laneEnds, item.startMinutes);
      laneEnds[lane] = item.endMinutes;
      const top = Math.max(0, Math.min(100, (item.startMinutes / (hourSpan * 60)) * 100));
      const height = Math.max(4, Math.min(100 - top, ((item.endMinutes - item.startMinutes) / (hourSpan * 60)) * 100));
      return { ...item, height, lane, laneCount: 1, top };
    })
    .map((item, _index, positioned) => ({ ...item, laneCount: Math.max(1, ...positioned.map((candidate) => candidate.lane + 1)) }));
}

function firstAvailableTimeLane(laneEnds: readonly number[], startMinutes: number) {
  const lane = laneEnds.findIndex((endMinutes) => endMinutes <= startMinutes);
  return lane === -1 ? laneEnds.length : lane;
}

function eventToneClass(event: CalendarEvent) {
  if (event.tone === 'stay') return 'border-[#b8cfad] bg-[#dfeedd]/90 text-[#223126]';
  if (event.tone === 'daycare') return 'border-[#e0bdae] bg-[#f8e2da]/90 text-[#4f3129]';
  const category = (event.source as Appointment).serviceCategory;
  if (category === 'transportation') return 'border-[#b8d4d8] bg-[#e8f3f5]/95 text-[#244b50]';
  if (category === 'spa') return 'border-[#d9c5df] bg-[#f4eef8]/95 text-[#594263]';
  if (category === 'grooming') return 'border-[#dfc8bd] bg-[#fbf1ec]/95 text-[#5d3d33]';
  return 'border-[#d6ded0] bg-white/90 text-[#334033]';
}

function CurrentHourMarker({ now = new Date(), startHour = 0, hourSpan = 24, timeFormat = '24h' }: { readonly now?: Date; readonly startHour?: number; readonly hourSpan?: number; readonly timeFormat?: TimeFormat }) {
  const minutes = (now.getHours() - startHour) * 60 + now.getMinutes();
  const top = Math.min(96, Math.max(4, (minutes / (hourSpan * 60)) * 100));
  return (
    <span className="pointer-events-none absolute left-2 right-2 z-10 border-t-2 border-[#cf7766]" style={{ top: `${top}%` }}>
      <span className="-mt-3 inline-flex rounded-md bg-[#cf7766] px-1.5 py-0.5 text-[10px] font-semibold text-white">{formatTime(now, timeFormat)}</span>
    </span>
  );
}

function TimelineGridLines() {
  return (
    <span className="pointer-events-none absolute inset-x-0 top-0 bottom-0 opacity-80">
      {Array.from({ length: 48 }, (_, chunk) => (
        <span
          key={chunk}
          className={`absolute left-0 right-0 border-t ${chunk % 2 === 0 ? 'border-[#d6ded0]' : 'border-dotted border-[#e7ece2]'}`}
          style={{ top: `${(chunk / 48) * 100}%` }}
        />
      ))}
    </span>
  );
}

function DayTimelineSummary({ date, events, timeFormat }: { readonly date: string; readonly events: readonly CalendarEvent[]; readonly timeFormat: TimeFormat }) {
  if (events.length === 0) return null;
  const first = sortEvents(events)[0];
  const last = sortEvents(events).at(-1);
  return (
    <div className="mt-4 rounded-md border border-[#d6ded0] bg-white p-3 text-sm text-[#334033]">
      <p className="text-xs font-semibold uppercase text-[#687461]">Rango del dia</p>
      <p className="mt-1 font-semibold">{formatDate(date)} - {eventStartTime(first, timeFormat)} - {last ? eventEndTime(last, timeFormat) : eventStartTime(first, timeFormat)}</p>
    </div>
  );
}

function DayTimeline({ date, events, onShowDetails, timeFormat }: { readonly date: string; readonly events: readonly CalendarEvent[]; readonly onShowDetails: (event: CalendarEvent) => void; readonly timeFormat: TimeFormat }) {
  const sorted = sortEvents(events);
  const hasMultiDayEvent = sorted.some((event) => eventDateStart(event) < date || eventDateEnd(event) > date);
  const firstHour = sorted.length ? Math.min(...sorted.map((event) => clampedEventRangeForDate(event, date).start.getHours())) : 8;
  const startHour = hasMultiDayEvent ? 0 : Math.max(0, firstHour);
  const hourSpan = hasMultiDayEvent ? 24 : 8;
  const chunks = Array.from({ length: hourSpan * 2 + 1 }, (_, index) => index);
  const layout = timedEventLayout(date, sorted, startHour, hourSpan);
  const now = useCurrentMinute();
  return (
    <div className="mt-4 max-h-96 overflow-y-auto rounded-md border border-[#d6ded0] bg-white">
      <div className={`relative ${hourSpan === 24 ? 'min-h-[960px]' : 'min-h-[640px]'}`}>
        {chunks.map((chunk) => {
          const hour = startHour + Math.floor(chunk / 2);
          const minute = chunk % 2 === 0 ? '00' : '30';
          return (
            <div key={chunk} className={`absolute left-0 right-0 border-t ${chunk % 2 === 0 ? 'border-[#d6ded0]' : 'border-dotted border-[#e7ece2]'}`} style={{ top: `${(chunk / (hourSpan * 2)) * 100}%` }}>
              <span className="absolute left-2 -mt-2 bg-white px-1 font-mono text-[10px] text-[#687461]">{formatClockParts(hour, minute, timeFormat)}</span>
            </div>
          );
        })}
        {layout.map((item) => {
          return (
            <button
              key={`${item.event.tone}-${item.event.id}-timeline-${item.lane}`}
              type="button"
              className={`absolute rounded-md border px-2 py-1 text-left text-xs shadow-sm hover:brightness-95 ${eventToneClass(item.event)}`}
              style={{
                left: `calc(4rem + ${(item.lane / item.laneCount) * 72}%)`,
                top: `${item.top}%`,
                width: `calc(${72 / item.laneCount}% - 4px)`,
                minHeight: `${item.height}%`,
              }}
              onClick={() => onShowDetails(item.event)}
            >
              <span className="block font-semibold">{eventTimeLabel(item.event, timeFormat)}</span>
              <span className="flex items-center gap-1"><CalendarEventIcon event={item.event} className="h-3.5 w-3.5 shrink-0" /><span className="break-words">{item.event.title}</span></span>
            </button>
          );
        })}
        {date === toDateInput(now) ? <CurrentHourMarker now={now} startHour={startHour} hourSpan={hourSpan} timeFormat={timeFormat} /> : null}
      </div>
    </div>
  );
}

function useCurrentMinute() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(interval);
  }, []);
  return now;
}

type CalendarActions = {
  readonly appointmentDone: ReturnType<typeof useMutation<Appointment, Error, string>>;
  readonly appointmentDelete: ReturnType<typeof useMutation<void, Error, string>>;
  readonly stayCheckOut: ReturnType<typeof useMutation<Stay, Error, string>>;
  readonly stayDelete: ReturnType<typeof useMutation<void, Error, string>>;
  readonly stayExtend: ReturnType<typeof useMutation<Stay, Error, { readonly id: string; readonly checkOutDate: string }>>;
  readonly daycareCheckOut: ReturnType<typeof useMutation<Daycare, Error, string>>;
  readonly daycareDelete: ReturnType<typeof useMutation<void, Error, string>>;
  readonly daycareExtend: ReturnType<typeof useMutation<Daycare, Error, { readonly id: string; readonly endAt: string }>>;
};

function DayDetailsPanel({
  date,
  events,
  totalEvents,
  eventsPagination,
  onEventsPageChange,
  onEventsPageSizeChange,
  selectedDayEvents,
  rangeFrom,
  rangeTo,
  onShowDetails,
  onConfirm,
  actions,
  timeFormat,
}: {
  readonly date: string;
  readonly events: CalendarEvent[];
  readonly totalEvents: number;
  readonly eventsPagination: ReturnType<typeof useControlledPagination<CalendarEvent>>;
  readonly onEventsPageChange: (page: number) => void;
  readonly onEventsPageSizeChange: (pageSize: number) => void;
  readonly selectedDayEvents: CalendarEvent[];
  readonly rangeFrom: string;
  readonly rangeTo: string;
  readonly onShowDetails: (event: CalendarEvent) => void;
  readonly onConfirm: (state: ConfirmState) => void;
  readonly actions: CalendarActions;
  readonly timeFormat: TimeFormat;
}) {
  return (
    <section className="rounded-md border border-[#d6ded0] bg-[#fbfcfa] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-[#223126]">Eventos del rango</h2>
          <p className="mt-1 text-sm text-[#687461]">{formatDate(rangeFrom)} a {formatDate(rangeTo)} · {totalEvents} evento(s)</p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Link className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-[#d6ded0] bg-white px-3 text-sm font-medium text-[#334033] hover:bg-[#eef3ea]" to={`/admin/daycares/new?date=${encodeURIComponent(date)}`}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Agregar guarderia
        </Link>
        <Link className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-[#d6ded0] bg-white px-3 text-sm font-medium text-[#334033] hover:bg-[#eef3ea]" to={`/admin/stays/new?date=${encodeURIComponent(date)}`}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Agregar hotel
        </Link>
        <Link className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-[#d6ded0] bg-white px-3 text-sm font-medium text-[#334033] hover:bg-[#eef3ea]" to={`/admin/grooming/new?date=${encodeURIComponent(date)}`}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Agregar cita
        </Link>
      </div>
      <DayTimeline date={date} events={selectedDayEvents} onShowDetails={onShowDetails} timeFormat={timeFormat} />
      <DayTimelineSummary date={date} events={selectedDayEvents} timeFormat={timeFormat} />
      <div className="mt-4 overflow-x-auto rounded-md border border-[#d6ded0] bg-white">
        <Table>
          <thead><tr><Th>Evento</Th><Th>Fecha y hora</Th><Th>Cliente</Th><Th>Perros</Th><Th className="w-24">Acciones</Th></tr></thead>
          <tbody>
            {totalEvents === 0 ? <tr><Td colSpan={5} className="py-6 text-center text-[#687461]">No hay eventos en este rango.</Td></tr> : null}
            {events.map((event) => <DayEventRow key={`${event.tone}-${event.id}`} event={event} onShowDetails={onShowDetails} onConfirm={onConfirm} actions={actions} timeFormat={timeFormat} />)}
          </tbody>
        </Table>
      </div>
      {totalEvents > 0 ? (
        <PaginationControls
          {...eventsPagination}
          onPageChange={onEventsPageChange}
          onPageSizeChange={onEventsPageSizeChange}
        />
      ) : null}
    </section>
  );
}

function DayEventRow({
  event,
  onShowDetails,
  onConfirm,
  actions,
  timeFormat,
}: {
  readonly event: CalendarEvent;
  readonly onShowDetails: (event: CalendarEvent) => void;
  readonly onConfirm: (state: ConfirmState) => void;
  readonly actions: CalendarActions;
  readonly timeFormat: TimeFormat;
}) {
  const source = event.source;
  return (
    <tr className="transition hover:bg-[#f8faf6]">
      <Td>
        <button type="button" className="min-w-48 text-left" onClick={() => onShowDetails(event)}>
          <span className="flex items-center gap-2 font-semibold text-[#334033]">
            <CalendarEventIcon event={event} className="h-4 w-4" />
            {event.title}
          </span>
          <span className="mt-1 block max-w-72 truncate text-xs text-[#687461]">{event.detail}</span>
        </button>
      </Td>
      <Td className="whitespace-nowrap text-xs">{eventDateTimeLabel(event, timeFormat)}</Td>
      <Td>{event.customerName}</Td>
      <Td>{event.dogNames}</Td>
      <Td>
        <div className="flex items-center gap-1">
        <Button type="button" variant="secondary" className="h-8 w-8 px-0" title="Ver detalles" aria-label="Ver detalles" onClick={() => onShowDetails(event)}>
          <Eye className="h-4 w-4" aria-hidden="true" />
        </Button>
        <ActionMenu
          groups={[
            {
              label: 'Gestion',
              children: (
                <>
                  <MenuLink to={event.href} icon={<Pencil className="h-4 w-4" aria-hidden="true" />}>Editar</MenuLink>
                  <MenuLink to={`/admin/payments?customerId=${encodeURIComponent(event.customerId)}&sourceType=${event.tone === 'stay' ? 'stay' : event.tone === 'daycare' ? 'daycare' : 'service'}&sourceId=${encodeURIComponent(event.id)}`} icon={<CreditCard className="h-4 w-4" aria-hidden="true" />}>Pagar</MenuLink>
                </>
              ),
            },
            {
              label: 'Flujo',
              children: event.tone === 'appointment' ? (
                <MenuButton disabled={(source as Appointment).status === 'done' || actions.appointmentDone.isPending} onClick={() => actions.appointmentDone.mutate(event.id)} icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}>Marcar terminada</MenuButton>
              ) : event.tone === 'stay' ? (
                <MenuButton disabled={(source as Stay).status !== 'checked-in' || actions.stayCheckOut.isPending} onClick={() => actions.stayCheckOut.mutate(event.id)} icon={<LogOut className="h-4 w-4" aria-hidden="true" />}>Confirmar salida</MenuButton>
              ) : (
                <MenuButton disabled={!['checked-in', 'active'].includes((source as Daycare).status) || actions.daycareCheckOut.isPending} onClick={() => actions.daycareCheckOut.mutate(event.id)} icon={<LogOut className="h-4 w-4" aria-hidden="true" />}>Confirmar salida</MenuButton>
              ),
            },
            ...(event.tone === 'stay' || event.tone === 'daycare' ? [{
              label: 'Ajustar tiempo',
              children: event.tone === 'stay' ? (
                <>
                  <MenuButton onClick={() => actions.stayExtend.mutate({ id: event.id, checkOutDate: addDays((source as Stay).checkOutDate, 1) })}>+1 dia</MenuButton>
                  <MenuButton onClick={() => actions.stayExtend.mutate({ id: event.id, checkOutDate: addDays((source as Stay).checkOutDate, 2) })}>+2 dias</MenuButton>
                </>
              ) : (
                <>
                  <MenuButton onClick={() => actions.daycareExtend.mutate({ id: event.id, endAt: addHours((source as Daycare).endAt ?? (source as Daycare).startAt, 1) })}>+1 hora</MenuButton>
                  <MenuButton onClick={() => actions.daycareExtend.mutate({ id: event.id, endAt: addHours((source as Daycare).endAt ?? (source as Daycare).startAt, 2) })}>+2 horas</MenuButton>
                </>
              ),
            }] : []),
            {
              label: 'Riesgo',
              tone: 'danger',
              children: (
                <MenuButton
                  danger
                  onClick={() => onConfirm({
                    title: `Eliminar ${event.tone === 'stay' ? 'hotel' : event.tone === 'daycare' ? 'guarderia' : 'cita'}`,
                    body: `Eliminar ${event.title}?`,
                    confirmLabel: 'Eliminar',
                    onConfirm: () => {
                      if (event.tone === 'stay') actions.stayDelete.mutate(event.id);
                      else if (event.tone === 'daycare') actions.daycareDelete.mutate(event.id);
                      else actions.appointmentDelete.mutate(event.id);
                    },
                  })}
                  icon={<Trash2 className="h-4 w-4" aria-hidden="true" />}
                >
                  Eliminar
                </MenuButton>
              ),
            },
          ]}
        />
        </div>
      </Td>
    </tr>
  );
}

function buildEvents({ appointments, customers, daycares, dogs, services, stays }: {
  readonly appointments: Appointment[];
  readonly customers: Customer[];
  readonly daycares: Daycare[];
  readonly dogs: Dog[];
  readonly services: Service[];
  readonly stays: Stay[];
}): CalendarEvent[] {
  return [
    ...stays.map((stay) => ({
      id: stay.id,
      date: stay.checkInDate,
      startAt: `${stay.checkInDate}T00:00:00`,
      endAt: `${stay.checkOutDate}T23:59:00`,
      title: `Hotel: ${dogNames(stay.dogIds ?? [stay.dogId], dogs)}`,
      detail: `${formatDate(stay.checkInDate)} 00:00 a ${formatDate(stay.checkOutDate)} 23:59`,
      href: `/admin/stays/${stay.id}/edit`,
      tone: 'stay' as const,
      customerId: stay.customerId,
      customerName: customerName(stay.customerId, customers),
      dogIds: stay.dogIds ?? [stay.dogId],
      dogNames: dogNames(stay.dogIds ?? [stay.dogId], dogs),
      hasTransportService: hasTransportService(serviceIdsForCalendarSource(stay), services),
      source: stay,
    })),
    ...daycares.map((daycare) => ({
      id: daycare.id,
      date: daycare.startAt.slice(0, 10),
      startAt: daycare.startAt,
      endAt: daycare.endAt,
      title: `Guarderia: ${dogNames(daycare.dogIds ?? [daycare.dogId], dogs)}`,
      detail: daycare.billingMode === 'entrance-package' ? 'Paquete por entradas' : 'Por hora',
      href: `/admin/daycares/${daycare.id}/edit`,
      tone: 'daycare' as const,
      customerId: daycare.customerId,
      customerName: customerName(daycare.customerId, customers),
      dogIds: daycare.dogIds ?? [daycare.dogId],
      dogNames: dogNames(daycare.dogIds ?? [daycare.dogId], dogs),
      hasTransportService: hasTransportService(serviceIdsForCalendarSource(daycare), services),
      source: daycare,
    })),
    ...appointments.map((appointment) => ({
      id: appointment.id,
      date: appointment.scheduledAt.slice(0, 10),
      startAt: appointment.scheduledAt,
      endAt: appointment.scheduledAt,
      title: `${appointmentLabel(appointment)}: ${dogNames(appointment.dogIds, dogs)}`,
      detail: `Servicios: ${appointment.serviceIds.map((serviceId) => services.find((service) => service.id === serviceId)?.name ?? serviceId).join(', ')}`,
      href: `/admin/grooming/${appointment.id}/edit`,
      tone: 'appointment' as const,
      customerId: appointment.customerId,
      customerName: customerName(appointment.customerId, customers),
      dogIds: appointment.dogIds,
      dogNames: dogNames(appointment.dogIds, dogs),
      hasTransportService: hasTransportService(appointment.serviceIds, services),
      source: appointment,
    })),
  ];
}

function serviceIdsForCalendarSource(source: Stay | Daycare) {
  return [...new Set([...(source.serviceIds ?? []), ...Object.values(source.dogServiceIds ?? {}).flat()])];
}

function hasTransportService(serviceIds: readonly string[], services: readonly Service[]) {
  return serviceIds.some((serviceId) => {
    const service = services.find((item) => item.id === serviceId);
    return service?.category === 'transportation' || /transport/i.test(service?.name ?? '');
  });
}

function appointmentLabel(appointment: Appointment) {
  if (appointment.serviceCategory === 'transportation') return 'Transporte';
  if (appointment.serviceCategory === 'grooming') return 'Grooming';
  if (appointment.serviceCategory === 'spa') return 'Spa';
  return 'Cita';
}

function EventDetailsDialog({ event, showCompletedBy, onClose }: { readonly event: CalendarEvent | null; readonly showCompletedBy: boolean; readonly onClose: () => void }) {
  if (!event) return null;
  const source = event.source;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/30 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-md border border-[#d6ded0] bg-white p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-[#223126]">{event.title}</h2>
            <p className="mt-1 text-sm text-[#687461]">{event.detail}</p>
          </div>
          <Button type="button" variant="secondary" className="h-8 px-3" onClick={onClose}>Cerrar</Button>
        </div>
        <div className="mt-4 grid gap-3">
          {event.tone === 'stay' ? <TimeRange mode="date" start={(source as Stay).checkInDate} end={(source as Stay).checkOutDate} /> : null}
          {event.tone === 'daycare' ? <TimeRange start={(source as Daycare).startAt} end={(source as Daycare).endAt} /> : null}
          {event.tone === 'appointment' ? <TimeRange start={(source as Appointment).scheduledAt} /> : null}
          <DetailRow label="Tipo" value={event.tone === 'stay' ? 'Hotel' : event.tone === 'daycare' ? 'Guarderia' : appointmentLabel(source as Appointment)} />
          <DetailRow label="Estado" value={'status' in source ? source.status : 'Programada'} />
          <DetailRow label="Cliente" value={event.customerName} />
          <DetailRow label="Perros" value={event.dogNames} />
          {'notes' in source && source.notes ? <DetailRow label="Notas" value={source.notes} /> : null}
          {showCompletedBy && event.tone === 'appointment' && (source as Appointment).completedByName ? <DetailRow label="Terminada por" value={(source as Appointment).completedByName ?? ''} /> : null}
        </div>
        <div className="mt-5 flex justify-end">
          <Link className="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-[#334033] px-4 text-sm font-semibold text-white hover:bg-[#223126]" to={event.href} onClick={onClose}>
            Ir al registro
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="rounded-md border border-[#eef3ea] bg-[#fbfcfa] p-3">
      <p className="text-xs font-semibold uppercase text-[#687461]">{label}</p>
      <p className="mt-1 text-sm text-[#223126]">{value}</p>
    </div>
  );
}

function MenuLink({ to, icon, children }: { readonly to: string; readonly icon?: React.ReactNode; readonly children: React.ReactNode }) {
  return <Link className="flex items-center gap-2 rounded-md px-2 py-2 text-[#334033] hover:bg-[#eef3ea]" to={to}>{icon}{children}</Link>;
}

function MenuButton({ onClick, icon, children, disabled, danger = false }: { readonly onClick?: () => void; readonly icon?: React.ReactNode; readonly children: React.ReactNode; readonly disabled?: boolean; readonly danger?: boolean }) {
  return (
    <button type="button" className={`flex items-center gap-2 rounded-md px-2 py-2 text-left hover:bg-[#eef3ea] disabled:cursor-not-allowed disabled:opacity-60 ${danger ? 'text-[#8a4a3a] hover:bg-[#fff8f8]' : 'text-[#334033]'}`} disabled={disabled} onClick={onClick}>
      {icon}
      {children}
    </button>
  );
}

function addDays(value: string, days: number) {
  const date = new Date(`${value}T12:00:00`);
  date.setDate(date.getDate() + days);
  return toDateInput(date);
}

function addHours(value: string, hours: number) {
  const date = new Date(value);
  date.setHours(date.getHours() + hours);
  return date.toISOString();
}

function dogNames(dogIds: readonly string[], dogs: Dog[]) {
  return dogIds.map((dogId) => dogs.find((dog) => dog.id === dogId)?.name ?? dogId).join(', ');
}

function customerName(customerId: string, customers: Customer[]) {
  const customer = customers.find((item) => item.id === customerId);
  return customer ? `${customer.firstName} ${customer.lastName}` : customerId;
}

function groupEvents(events: CalendarEvent[]) {
  const grouped = new Map<string, CalendarEvent[]>();
  for (const event of events) {
    for (const date of eventDates(event)) {
      grouped.set(date, [...(grouped.get(date) ?? []), { ...event, date }]);
    }
  }
  return grouped;
}

function eventDates(event: CalendarEvent) {
  const start = toDateInput(new Date(event.startAt));
  const end = toDateInput(new Date(event.endAt ?? event.startAt));
  const dates: string[] = [];
  for (let date = start; date <= end; date = addDays(date, 1)) dates.push(date);
  return dates;
}

function clampedEventRangeForDate(event: CalendarEvent, date: string) {
  const dayStart = new Date(`${date}T00:00:00`);
  const dayEnd = new Date(`${date}T23:59:59`);
  const start = new Date(Math.max(new Date(event.startAt).getTime(), dayStart.getTime()));
  const end = new Date(Math.min(new Date(event.endAt ?? event.startAt).getTime(), dayEnd.getTime()));
  return { start, end: end.getTime() <= start.getTime() ? new Date(start.getTime() + 30 * 60_000) : end };
}

function calendarRange(date: Date, view: CalendarView) {
  if (view === 'day') {
    const day = toDateInput(date);
    return { from: day, to: day };
  }
  if (view === 'week') {
    const days = calendarDays(date, 'week');
    return { from: days[0].date, to: days[days.length - 1].date };
  }
  return {
    from: toDateInput(new Date(date.getFullYear(), date.getMonth(), 1)),
    to: toDateInput(new Date(date.getFullYear(), date.getMonth() + 1, 0)),
  };
}

function calendarDays(anchor: Date, view: CalendarView) {
  if (view === 'day') return [{ date: toDateInput(anchor), inMonth: true }];
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const start = view === 'week' ? new Date(anchor) : new Date(first);
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  return Array.from({ length: view === 'week' ? 7 : 42 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return { date: toDateInput(date), inMonth: date.getMonth() === anchor.getMonth() };
  });
}

function sortEvents(events: readonly CalendarEvent[]) {
  return [...events].sort((left, right) => new Date(left.startAt).getTime() - new Date(right.startAt).getTime());
}

function sortEventsNewestFirst(events: readonly CalendarEvent[]) {
  return [...events].sort((left, right) => new Date(right.startAt).getTime() - new Date(left.startAt).getTime());
}

function eventTimeLabel(event: CalendarEvent, timeFormat: TimeFormat = '24h') {
  return `${eventStartTime(event, timeFormat)} - ${eventEndTime(event, timeFormat)}`;
}

function eventDateTimeLabel(event: CalendarEvent, timeFormat: TimeFormat) {
  const startDate = eventDateStart(event);
  const endDate = eventDateEnd(event);
  const dateLabel = startDate === endDate ? formatDate(startDate) : `${formatDate(startDate)} - ${formatDate(endDate)}`;
  return `${dateLabel} · ${eventTimeLabel(event, timeFormat)}`;
}

function eventStartTime(event: CalendarEvent, timeFormat: TimeFormat = '24h') {
  return formatTime(new Date(event.startAt), timeFormat);
}

function eventEndTime(event: CalendarEvent, timeFormat: TimeFormat = '24h') {
  return formatTime(new Date(event.endAt ?? event.startAt), timeFormat);
}

function formatTime(date: Date, timeFormat: TimeFormat) {
  return new Intl.DateTimeFormat('es-MX', { hour: '2-digit', hour12: timeFormat === '12h', minute: '2-digit' }).format(date);
}

function formatClockParts(hour: number, minute: string, timeFormat: TimeFormat) {
  if (timeFormat === '24h') return `${String(hour).padStart(2, '0')}:${minute}`;
  const normalizedHour = ((hour + 11) % 12) + 1;
  return `${String(normalizedHour).padStart(2, '0')}:${minute} ${hour >= 12 ? 'pm' : 'am'}`;
}

function calendarTitle(date: Date, view: CalendarView) {
  if (view === 'day') return date.toLocaleDateString('es-MX', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  if (view === 'week') {
    const days = calendarDays(date, 'week');
    return `${formatDate(days[0].date)} - ${formatDate(days[days.length - 1].date)}`;
  }
  return date.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
}

function weekdayLabel(date: Date) {
  return date.toLocaleDateString('es-MX', { weekday: 'short' });
}
